import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IOrderItem } from './models/order-item.model';
import { CRequiredEligibleVariations, IVariationRatio } from '../product/models/product-variation-ratio.model';
import { ProductHelperService } from '../product/product-helper.service';
import { DateFormatService } from '../date/date-format.service';
import { ProductListService } from '@core/product/product-list.service';

@Injectable({
  providedIn: 'root'
})
export class OrderTicketRatiosService {
  constructor(
    private translateService: TranslateService,
    private productHelperService: ProductHelperService,
    private dateService: DateFormatService,
    private productListService: ProductListService
  ) {}

  /** Constructs a message displaying the ticket ratio rule and a list of eligible variations that can fulfill the rule */
  buildAlertMessage(item: IOrderItem) {
    const selectedRatio = this.getSelectedRatio(item);

    if (!selectedRatio) {
      return '';
    }

    const eligibleVariationNames = this.getEligibleVariationNames(selectedRatio, item);
    const headingString =
      eligibleVariationNames.length > 1
        ? 'PRODUCT_MODAL.TICKET_RATIOS.FOR_EVERY_REQUIRED_TICKET_MULTIPLE'
        : 'PRODUCT_MODAL.TICKET_RATIOS.FOR_EVERY_REQUIRED_TICKET_SINGLE';
    const alertHeader = this.translateService.instant(headingString, {
      RATIO: selectedRatio.dependentQuantity,
      TICKET: this.getSelectedVariationName(item, selectedRatio.dependentProductId)
    });

    const eligibleVariationsString = eligibleVariationNames.join(
      ` ${this.translateService.instant('GENERIC.OR').toLowerCase()} `
    );
    return `${alertHeader} ${eligibleVariationsString}`;
  }

  /** Returns the ticket ratio rule attached to the parent product of a given basket item */
  getSelectedRatio(item: IOrderItem) {
    const parentProduct = this.productListService.getParentProductById(item.parentProductId);
    return parentProduct?.variationRatioRequirements?.find((ratio) => ratio.dependentProductId === item.productId);
  }

  /** Returns the name of the variation associated with an item in basket */
  getSelectedVariationName(item: IOrderItem, ticketId: string, appendOne = false) {
    const parentProduct = this.productListService.getParentProductById(item.parentProductId);
    const ticket = parentProduct?.products.find((ticket) => ticket.id === ticketId);
    if (!ticket) {
      return '';
    }

    const eligibleVariationString = appendOne
      ? this.translateService.instant('PRODUCT_MODAL.TICKET_RATIOS.ELIGIBLE_VARIATION', {
          NUMBER: CRequiredEligibleVariations,
          VARIATION_NAME: ticket.name
        })
      : ticket.name;
    return eligibleVariationString;
  }

  /** Returns an array of the names of all variations that can be added to cart to fulfill the ratio rule */
  getEligibleVariationNames(selectedRatio: IVariationRatio, item: IOrderItem) {
    return selectedRatio.eligibleVariations.map((variation) =>
      this.getSelectedVariationName(item, variation.productId, true)
    );
  }

  /** Determines if an item in the basket has a ticket ratio rule and calculates if the requirement has been met */
  isInvalidTicketRatio(item: IOrderItem, orderItems: IOrderItem[]): boolean {
    const parentProduct = this.productListService.getParentProductById(item.parentProductId);
    if (!parentProduct?.variationRatioRequirements) {
      return false;
    }

    const selectedRatio = this.getSelectedRatio(item);

    if (!selectedRatio) {
      return false;
    }

    const dependentTicketsAdded = orderItems.reduce((count, orderItem) => {
      return this.isMatchingSession(item, orderItem, selectedRatio.dependentProductId)
        ? count + orderItem.quantity
        : count;
    }, 0);

    const eligibleTicketsAdded = selectedRatio.eligibleVariations.reduce((count, variation) => {
      const matchingVariation = orderItems.find((orderItem) =>
        this.isMatchingSession(item, orderItem, variation.productId)
      );
      return matchingVariation ? count + matchingVariation.quantity : count;
    }, 0);

    return eligibleTicketsAdded < Math.ceil(dependentTicketsAdded / selectedRatio.dependentQuantity);
  }

  /**
   * Compares if two order items that have a ticket ratio rule applied to them share the same session date and time.
   * Returns true if two standard passes share the same date, or true if two session passes return the same date and time.
   */
  isMatchingSession(item: IOrderItem, orderItem: IOrderItem, variationProductId: string) {
    if (orderItem.productId !== variationProductId) {
      return false;
    }

    const parentProduct = this.productListService.getParentProductById(item.parentProductId);
    if (parentProduct?.isItemDateBased && !parentProduct.isItemSessionBased) {
      return this.dateService.isSameDay(item.bookingDate, orderItem.bookingDate);
    }

    return this.productHelperService.isSameSession(item.session, orderItem.session);
  }
}
